import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';

const ToastContext = createContext({ showToast: () => {} });
export const useToast = () => useContext(ToastContext);

// white surface, ink text; the type is a small coloured dot (no glow / tint)
const TYPE_STYLE = {
  error:   { dot: 'var(--red)' },
  success: { dot: 'var(--rind)' },
  info:    { dot: 'var(--dim)' },
};

let _id = 0;

function ToastCard({ toast, onClose }) {
  const [leaving, setLeaving] = useState(false);
  const s = TYPE_STYLE[toast.type] || TYPE_STYLE.error;

  const close = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onClose(toast.id), 260);
  }, [toast.id, onClose]);

  useEffect(() => {
    if (!toast.duration) return;
    const tm = setTimeout(close, toast.duration);
    return () => clearTimeout(tm);
  }, [toast.duration, close]);

  return (
    <div
      className={`toast-card ${leaving ? 'leaving' : ''}`}
      style={{
        position: 'relative', padding: '13px 48px 13px 16px', borderRadius: 20,
        border: '1px solid var(--rule)',
        background: 'var(--card)',
        boxShadow: '0 1px 4px rgba(38,32,26,.12)',
        fontFamily: 'var(--f-body)', fontWeight: 500, fontSize: 13, lineHeight: 1.5,
        color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 10,
        pointerEvents: 'auto',
      }}
    >
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: s.dot }} />
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button
        onClick={close}
        aria-label="Dismiss"
        className="toast-x"
        style={{
          position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: 999,
          border: 'none', background: 'var(--card)',
          color: 'var(--dim)', fontSize: 18, lineHeight: 1, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >×</button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const isMobile = useIsMobile();
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const remove = useCallback((id) => setToasts(ts => ts.filter(t => t.id !== id)), []);

  const showToast = useCallback((message, opts = {}) => {
    if (!message) return;
    const type = opts.type || 'error';
    // de-dupe: if the same message is already on screen, don't stack it again
    if (toastsRef.current.some(t => t.message === message && t.type === type)) return;
    const id = ++_id;
    setToasts(ts => [...ts, { id, message, type, duration: opts.duration ?? 10000 }]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'fixed', top: isMobile ? 12 : 76, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, width: 'min(440px, calc(100vw - 32px))',
          display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none',
        }}
      >
        {toasts.map(t => <ToastCard key={t.id} toast={t} onClose={remove} />)}
      </div>
    </ToastContext.Provider>
  );
}
